import React, { useState, useRef, useEffect } from 'react';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import type { MultiSelect as MultiSelectRef } from 'primereact/multiselect';
import './multi-select.scss';

interface Option {
  name: string;
  code?: string;
  avatar?: string;
}

const OptionItemTemplate: React.FC<{ option: Option }> = ({ option }) => (
  <div className="multi-select-item-with-avatar flex">
    {option.avatar && <span className={`avatar ${option.avatar}`} />}
    <span className="option-label">{option.name}</span>
  </div>
);

const truncateName = (name: string, maxLength = 15): string => {
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}…`;
};

const SelectedItemTemplate: React.FC<{ option: Option }> = ({ option }) => {
  if (!option || typeof option !== 'object') return null;
  return (
    <div className="selected-option-chip">
      {option.avatar && <span className={`avatar ${option.avatar}`} />}
      <span className="selected-option-label">{truncateName(option.name)}</span>
    </div>
  );
};

interface MultiSelectComponentProps {
  options: Option[];
  value?: Option[] | null;
  placeholder?: string;
  maxSelectedLabels?: number;
  onSelectionChange?: (selectedOptions: Option[] | null) => void;
  className?: string;
  filter?: boolean;
  hideHeader?: boolean;
  panelClassName?: string;
}

const MultiSelectComponent: React.FC<MultiSelectComponentProps> = ({
  options,
  placeholder = 'Select Options',
  maxSelectedLabels = 5,
  onSelectionChange,
  className,
  filter = true,
  hideHeader = false,
  panelClassName,
  value = null,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[] | null>(value || null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const multiSelectRef = useRef<MultiSelectRef>(null);

  const handleSelectionChange = (e: MultiSelectChangeEvent) => {
    const newValue = e.value && e.value.length > 0 ? e.value : null;
    setSelectedOptions(newValue);
    setHasSelection(!!newValue && newValue.length > 0);
    if (onSelectionChange) onSelectionChange(newValue);
    requestAnimationFrame(() => multiSelectRef.current?.show());
  };
  const handleDropdownOpen = () => {
    setIsOpen(true);
  };

  const handleDropdownClose = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    setSelectedOptions(value || null);
  }, [value]);


  return (
    <div className="custom-multiselect-wrapper">
      <MultiSelect
        ref={multiSelectRef}
        value={selectedOptions}
        onChange={handleSelectionChange}
        onShow={handleDropdownOpen}
        onHide={handleDropdownClose}
        filter={filter}
        options={options}
        display="chip"
        optionLabel="name"
        placeholder={placeholder}
        maxSelectedLabels={maxSelectedLabels}
        panelClassName={`multi-select-dropdown-panel ${panelClassName ?? ''} ${hideHeader ? 'hide-header' : ''}`.trim()}
        className={`${className} ${isOpen && !hasSelection ? 'open' : 'closed'}`}
        dropdownIcon={isOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
        itemTemplate={(option) => <OptionItemTemplate option={option} />}
        selectedItemTemplate={(option) => <SelectedItemTemplate option={option} />}
        appendTo="self"
      />
      {!hasSelection && !selectedOptions?.length && (
        <button
          type="button"
          className="empty-placeholder-overlay"
          onClick={() => multiSelectRef.current?.show()}
          aria-label={placeholder}
        >
          {placeholder}
        </button>
      )}
    </div>
  );
};

export default MultiSelectComponent;
